/**
 * Privacy-Compliant Analytics Module
 * 
 * Tracks basic app usage metrics without collecting personal information.
 * All data is stored locally and optionally sent to analytics service.
 * 
 * Complies with Apple's privacy guidelines and GDPR requirements.
 */

import { Capacitor } from '@capacitor/core';

export interface AnalyticsEvent {
    name: string;
    timestamp: number;
    properties?: Record<string, any>;
}

export interface SessionInfo {
    sessionId: string;
    startTime: number;
    endTime?: number;
    duration?: number;
    platform: string;
    isNative: boolean;
}

export interface AppMetrics {
    totalSessions: number;
    totalPlayTime: number; // in seconds
    gamesPlayed: number;
    averageGameTime: number;
    highScore: number;
    lastSessionDate: number;
    installDate: number;
    appVersion: string;
}

/**
 * Analytics service for tracking app usage
 * Privacy-first design - no personal information collected
 */
export class AnalyticsService {
    private static instance: AnalyticsService;
    private currentSession: SessionInfo | null = null;
    private isEnabled: boolean = true;
    private storageKey = 'analytics_data';
    private metricsKey = 'app_metrics';

    private constructor() {
        this.initialize();
    }

    public static getInstance(): AnalyticsService {
        if (!AnalyticsService.instance) {
            AnalyticsService.instance = new AnalyticsService();
        }
        return AnalyticsService.instance;
    }

    /**
     * Initialize analytics service
     */
    private initialize(): void {
        // Check if analytics is enabled (respects user privacy settings)
        const analyticsEnabled = localStorage.getItem('analytics_enabled');
        if (analyticsEnabled === 'false') {
            this.isEnabled = false;
            console.log('Analytics disabled by user preference');
            return;
        }

        // Initialize metrics if first launch
        const metrics = this.getMetrics();
        if (!metrics.installDate) {
            this.setMetric('installDate', Date.now());
            this.setMetric('appVersion', this.getAppVersion());
        }

        // Start session tracking
        this.startSession();
    }

    /**
     * Get current app version
     */
    private getAppVersion(): string {
        // In production, this would come from package.json or native app info
        return '0.1.0';
    }

    /**
     * Enable or disable analytics
     */
    public setAnalyticsEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
        localStorage.setItem('analytics_enabled', enabled.toString());

        if (!enabled && this.currentSession) {
            this.endSession();
        }
    }

    /**
     * Check if analytics is enabled
     */
    public isAnalyticsEnabled(): boolean {
        return this.isEnabled;
    }

    /**
     * Start a new session
     */
    private startSession(): void {
        if (!this.isEnabled) return;

        this.currentSession = {
            sessionId: this.generateSessionId(),
            startTime: Date.now(),
            platform: Capacitor.getPlatform(),
            isNative: Capacitor.isNativePlatform()
        };

        this.incrementMetric('totalSessions');
        this.setMetric('lastSessionDate', Date.now());

        console.log('Analytics session started:', this.currentSession.sessionId);
    }

    /**
     * End current session
     */
    public endSession(): void {
        if (!this.isEnabled || !this.currentSession) return;

        const endTime = Date.now();
        const duration = Math.floor((endTime - this.currentSession.startTime) / 1000);

        this.currentSession.endTime = endTime;
        this.currentSession.duration = duration;

        // Update total play time
        const currentPlayTime = this.getMetrics().totalPlayTime || 0;
        this.setMetric('totalPlayTime', currentPlayTime + duration);

        console.log('Analytics session ended:', {
            sessionId: this.currentSession.sessionId,
            duration: `${duration}s`
        });

        this.currentSession = null;
    }

    /**
     * Generate unique session ID
     */
    private generateSessionId(): string {
        return `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    }

    /**
     * Track an event
     */
    public trackEvent(eventName: string, properties?: Record<string, any>): void {
        if (!this.isEnabled) return;

        const event: AnalyticsEvent = {
            name: eventName,
            timestamp: Date.now(),
            properties: properties || {}
        };

        // Add session context
        if (this.currentSession) {
            event.properties!.sessionId = this.currentSession.sessionId;
        }

        // Log event (in production, this could send to analytics service)
        console.log('Analytics event:', event.name, event.properties);

        // Store event locally (with size limit to prevent storage bloat)
        this.storeEvent(event);
    }

    /**
     * Store event locally
     */
    private storeEvent(event: AnalyticsEvent): void {
        try {
            const eventsJson = localStorage.getItem(this.storageKey);
            const events: AnalyticsEvent[] = eventsJson ? JSON.parse(eventsJson) : [];

            // Add new event
            events.push(event);

            // Keep only last 100 events to prevent storage overflow
            const recentEvents = events.slice(-100);

            localStorage.setItem(this.storageKey, JSON.stringify(recentEvents));
        } catch (error) {
            console.warn('Failed to store analytics event:', error);
        }
    }

    /**
     * Get stored events
     */
    public getEvents(): AnalyticsEvent[] {
        try {
            const eventsJson = localStorage.getItem(this.storageKey);
            return eventsJson ? JSON.parse(eventsJson) : [];
        } catch (error) {
            console.warn('Failed to retrieve analytics events:', error);
            return [];
        }
    }

    /**
     * Clear stored events
     */
    public clearEvents(): void {
        localStorage.removeItem(this.storageKey);
        console.log('Analytics events cleared');
    }

    /**
     * Get app metrics
     */
    public getMetrics(): AppMetrics {
        try {
            const metricsJson = localStorage.getItem(this.metricsKey);
            return metricsJson ? JSON.parse(metricsJson) : {
                totalSessions: 0,
                totalPlayTime: 0,
                gamesPlayed: 0,
                averageGameTime: 0,
                highScore: 0,
                lastSessionDate: 0,
                installDate: 0,
                appVersion: this.getAppVersion()
            };
        } catch (error) {
            console.warn('Failed to retrieve app metrics:', error);
            return {
                totalSessions: 0,
                totalPlayTime: 0,
                gamesPlayed: 0,
                averageGameTime: 0,
                highScore: 0,
                lastSessionDate: 0,
                installDate: 0,
                appVersion: this.getAppVersion()
            };
        }
    }

    /**
     * Set a metric value
     */
    private setMetric(key: keyof AppMetrics, value: any): void {
        const metrics = this.getMetrics();
        (metrics as any)[key] = value;
        localStorage.setItem(this.metricsKey, JSON.stringify(metrics));
    }

    /**
     * Increment a metric counter
     */
    private incrementMetric(key: keyof AppMetrics, amount: number = 1): void {
        const metrics = this.getMetrics();
        const currentValue = (metrics as any)[key] || 0;
        (metrics as any)[key] = currentValue + amount;
        localStorage.setItem(this.metricsKey, JSON.stringify(metrics));
    }

    /**
     * Track game start
     */
    public trackGameStart(): void {
        this.trackEvent('game_start');
    }

    /**
     * Track game end
     */
    public trackGameEnd(score: number, duration: number, longestChain: number): void {
        this.trackEvent('game_end', {
            score,
            duration,
            longestChain
        });

        // Update metrics
        this.incrementMetric('gamesPlayed');

        const metrics = this.getMetrics();
        if (score > metrics.highScore) {
            this.setMetric('highScore', score);
        }

        // Update average game time
        const totalGames = metrics.gamesPlayed + 1;
        const currentAvg = metrics.averageGameTime || 0;
        const newAvg = Math.floor((currentAvg * (totalGames - 1) + duration) / totalGames);
        this.setMetric('averageGameTime', newAvg);
    }

    /**
     * Track screen view
     */
    public trackScreen(screenName: string): void {
        this.trackEvent('screen_view', { screenName });
    }

    /**
     * Track button/action
     */
    public trackAction(actionName: string, properties?: Record<string, any>): void {
        this.trackEvent('action', {
            actionName,
            ...properties
        });
    }

    /**
     * Track error
     */
    public trackError(error: Error, context?: string): void {
        this.trackEvent('error', {
            message: error.message,
            stack: error.stack,
            context
        });
    }

    /**
     * Get analytics summary for debugging
     */
    public getSummary(): {
        enabled: boolean;
        currentSession: SessionInfo | null;
        metrics: AppMetrics;
        eventCount: number;
    } {
        return {
            enabled: this.isEnabled,
            currentSession: this.currentSession,
            metrics: this.getMetrics(),
            eventCount: this.getEvents().length
        };
    }

    /**
     * Export analytics data (for user data requests)
     */
    public exportData(): {
        metrics: AppMetrics;
        events: AnalyticsEvent[];
    } {
        return {
            metrics: this.getMetrics(),
            events: this.getEvents()
        };
    }

    /**
     * Clear all analytics data
     */
    public clearAllData(): void {
        localStorage.removeItem(this.storageKey);
        localStorage.removeItem(this.metricsKey);
        localStorage.removeItem('analytics_enabled');
        console.log('All analytics data cleared');
    }
}

// Export singleton instance
export const Analytics = AnalyticsService.getInstance();
